import { Suspense, lazy, useEffect, useState } from 'react';
import { registerReauthHandler } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useReauthStore } from '../../store/reauthStore';

/**
 * Trda navigacija (polno nalaganje) — izvozena, da jo testi lahko prestrežejo.
 * Polno nalaganje je namenoma: ob odjavi zavrže tudi predpomnilnik poizvedb in
 * vse stanje strani, da nič ne preide k naslednjemu uporabniku.
 */
export const sessionNavigation = {
  replace: (url: string) => window.location.replace(url),
};

const loadContent = () => import('./SessionExpiredDialogContent');
const SessionExpiredDialogContent = lazy(loadContent);

/**
 * T2376 — okno »Seja je potekla«; T3108 — lupina, ki ga naloži leno.
 *
 * Rokovalnik prestreznika se registrira tu, ob priklopu, ne v oknu: lupina je
 * priklopljena v vseh vejah `App` (T2689), okno samo pa s sabo nosi sklad
 * Radix Dialog in dvostopenjsko prijavo, ki ju začetni graf ne potrebuje.
 *
 * Okno se po prijavi naloži v ozadju, ko je brskalnik nezaposlen — ko seja
 * poteče, je kos že v pomnilniku. Sicer bi po novem deployu (stari kos ne
 * obstaja več) zadržane zahteve obvisele brez okna.
 */
const SessionExpiredDialog = () => {
  const open = useReauthStore((s) => s.open);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Enkrat priklopljeno okno ostane priklopljeno (prej je bilo vedno).
  const [mounted, setMounted] = useState(false);
  if (open && !mounted) setMounted(true);

  useEffect(() => {
    registerReauthHandler(() => {
      const { user, isAuthenticated } = useAuthStore.getState();
      if (!user || !isAuthenticated) return null;
      return useReauthStore.getState().request({ id: user.id, email: user.email });
    });
    return () => registerReauthHandler(null);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const prefetch = () => void loadContent().catch(() => undefined);
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(prefetch, 3000);
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <SessionExpiredDialogContent />
    </Suspense>
  );
};

export default SessionExpiredDialog;
